'use client';
import { useEffect } from 'react';
import { useRouter } from 'next/navigation';
import CallMergeIcon from '@mui/icons-material/CallMerge';
import { Box, Chip } from '@mui/material';

import {
  ComponentErrorBoundary,
  FeaturedMarketsCarousel,
  FeaturedMarketsCarouselSkeleton,
  TradeModal,
} from '@/components';
import { useOpenAuthModal } from '@/hooks';
import type { UseHomeReturn } from '@/hooks/Home/useHome';
import { useMixpanel } from '@/hooks/useMixpanel';
import { toggleSelectMode, useAppDispatch, useAppSelector } from '@/store';
import { useAuth } from '@/store/hooks';
import { ComboSelectionBar, HomeContent, SearchAndFilters } from './subcomponents';

import { ROUTES } from '@/constants';
import { SORT_OPTIONS } from '@/constants/filters.constants';

const QUICK_SORTS = [
  { label: 'Volume', value: SORT_OPTIONS.VOLUME_24H },
  { label: 'Newest', value: SORT_OPTIONS.NEWEST },
  { label: 'Ending soon', value: SORT_OPTIONS.ENDING_SOON },
];

interface HomeComponentProps {
  homeData: UseHomeReturn;
}

/**
 * HomeComponent
 *
 * The main presentational component for the Home page.
 * It manages the layout and integrates various subcomponents like navigation,
 * search/filters, and the main content area (markets list).
 *
 * It relies on the `useHome` hook for logic and state management.
 */
export const HomeComponent = ({ homeData }: HomeComponentProps) => {
  const router = useRouter();
  const openAuthModal = useOpenAuthModal();
  const { isAuthenticated } = useAuth();
  const dispatch = useAppDispatch();
  const isComboSelectMode = useAppSelector((state) => state.comboSelection.isSelectMode);

  // Destructure state, handlers, and refs from the useHome hook result
  const { state, handlers } = homeData;
  const {
    activeCategorySlug,
    tradeModalState,
    searchQuery,
    isFilterOpen,
    sortBy,
    frequency,
    status,
    events,
    isLoading,
    error,
    tags,
    isTagsLoading,
    selectedTag,
    hideSport,
    isTrendingPage,
  } = state;

  const {
    setSortBy,
    setFrequency,
    setStatus,
    handleSearchChange,
    handleSearchSubmit,
    handleFilterToggle,
    handleTradeClick,
    handleTradeModalClose,
    handleTagClick,
    setHideSport,
    refetch,
  } = handlers;

  const { trackLandingPageViewed, trackMarketListViewed } = useMixpanel();

  useEffect(() => {
    trackLandingPageViewed({
      device_type: navigator.userAgent,
      referrer_url: document.referrer,
    });
    trackMarketListViewed({
      category_filter: activeCategorySlug || 'all',
      sort_by: sortBy,
      total_markets_shown: events?.length || 0,
      topic_tag: selectedTag || 'none',
    });
  }, []);

  // Navigate to the bookmarks page; prompt sign-in if unauthenticated
  const handleBookmarksClick = () => {
    if (!isAuthenticated) {
      openAuthModal();
      return;
    }
    router.push(ROUTES.BOOKMARKS);
  };

  const shouldShowTradeModal = !!(
    tradeModalState.isOpen &&
    tradeModalState.market &&
    tradeModalState.outcome
  );

  return (
    <Box
      sx={{
        py: 2,
      }}
    >
      {/* Sticky Filters Section */}
      <Box
        sx={{
          position: 'sticky',
          top: { xs: '96px', sm: '100px', md: '104px' },
          zIndex: 10,
          bgcolor: 'background.default',
          mx: { xs: -1, sm: -1.5, md: -2, lg: -2.5 },
          px: { xs: 1, sm: 1.5, md: 2, lg: 2.5 },
          pb: 1,
          mt: -0.5,
        }}
      >
        {/* Search Bar and Filters Section */}
        <SearchAndFilters
          searchQuery={searchQuery}
          isFilterOpen={isFilterOpen}
          selectedTag={selectedTag}
          onSearchChange={handleSearchChange}
          onSearchSubmit={handleSearchSubmit}
          onFilterToggle={handleFilterToggle}
          onBookmarksOpen={handleBookmarksClick}
          tags={tags}
          isTagsLoading={isTagsLoading}
          onTagClick={handleTagClick}
          activeCategorySlug={activeCategorySlug}
          sortBy={sortBy}
          frequency={frequency}
          status={status}
          onSortByChange={setSortBy}
          onFrequencyChange={setFrequency}
          onStatusChange={setStatus}
          hideSport={hideSport}
          onHideSportChange={setHideSport}
          onClearFilters={handlers.handleResetFilters}
        />
      </Box>

      {/* Featured markets carousel — only on the Trending (home) tab, and hidden
          during search. Other category tabs never show it. */}
      {!searchQuery && isTrendingPage && (
        <ComponentErrorBoundary componentName="Featured Markets">
          {isLoading ? (
            <FeaturedMarketsCarouselSkeleton />
          ) : (
            <FeaturedMarketsCarousel events={events} />
          )}
        </ComponentErrorBoundary>
      )}

      {/* Quick sort tabs for the market grid — desktop only.
          Design (Landing.dc.html): 8px radius, 7px/14px padding, 13px/600 —
          a squared-off pill, not MUI Chip's default fully-rounded shape. */}
      {!isLoading && !error && !!events?.length && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 0.75,
            mb: 2,
          }}
        >
          <Chip
            icon={<CallMergeIcon sx={{ fontSize: '1rem' }} />}
            label={isComboSelectMode ? 'Exit combo mode' : 'Build a combo'}
            variant={isComboSelectMode ? 'filled' : 'outlined'}
            color={isComboSelectMode ? 'primary' : 'default'}
            onClick={() => dispatch(toggleSelectMode())}
            sx={{
              cursor: 'pointer',
              borderRadius: '8px',
              height: 'auto',
              '& .MuiChip-label': {
                fontSize: '13px',
                fontWeight: 600,
                padding: '7px 14px',
              },
            }}
          />
          <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 0.75 }}>
            {QUICK_SORTS.map((opt) => (
              <Chip
                key={opt.value}
                label={opt.label}
                variant={sortBy === opt.value ? 'filled' : 'outlined'}
                color={sortBy === opt.value ? 'primary' : 'default'}
                onClick={() => setSortBy(opt.value)}
                sx={{
                  cursor: 'pointer',
                  borderRadius: '8px',
                  height: 'auto',
                  '& .MuiChip-label': {
                    fontSize: '13px',
                    fontWeight: 600,
                    padding: '7px 14px',
                  },
                }}
              />
            ))}
          </Box>
        </Box>
      )}
      {/* Main Content Area (Loading, Error, or Market List) */}
      <HomeContent
        isLoading={isLoading}
        error={error}
        events={events}
        activeCategorySlug={activeCategorySlug}
        pagination={homeData.pagination}
        onTradeClick={handleTradeClick}
        onFetchNextPage={handlers.fetchNextPage}
        onRetry={refetch}
        isResolvedFilter={status === 'Resolved'}
      />

      {/* Trade Implementation Modal */}
      <TradeModal
        isOpen={shouldShowTradeModal}
        onClose={handleTradeModalClose}
        market={tradeModalState.market}
        outcome={tradeModalState.outcome}
        isBuyOrder={tradeModalState.isBuyOrder}
        forceDesktopVersion={tradeModalState.forceDesktopVersion}
      />

      <ComboSelectionBar />
    </Box>
  );
};
